'use client';

import { useState } from 'react';
import Link from 'next/link';
import { formatTimestampDate } from '@/lib/date-utils';
import { Heart, MessageCircle, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { FeedComment, FeedPost } from '@kit/community';
import {
  getPostComments,
  likePostAction,
  unlikePostAction,
} from '@kit/community/server/actions';
import { dancerPath, organizationPath } from '@kit/shared/handles';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Button } from '@kit/ui/button';
import { Card } from '@kit/ui/card';
import { toast } from '@kit/ui/sonner';

import { CommentsSection } from './comments-section';
import {
  AuditionEmbed,
  auditionEmbedVariant,
  EventEmbed,
  eventEmbedVariant,
  HeroIdentityChip,
  LinkAttachment,
  PostMediaGrid,
} from './post-attachments';
import { PostBody } from './post-body';
import { ShareDialog } from './share-dialog';

interface FeedPostCardProps {
  post: FeedPost;
  currentProfileId?: string | null;
}

export function FeedPostCard({ post, currentProfileId }: FeedPostCardProps) {
  const { t } = useTranslation();
  // Check if current user has liked this post
  const [isLiked, setIsLiked] = useState(post.isLiked ?? false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<FeedComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [showShareDialog, setShowShareDialog] = useState(false);

  const author = post.author;
  const postUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/home/community/post/${post.id}`
      : '';

  const profileLink = !author
    ? '#'
    : author.type === 'dancer'
      ? dancerPath(author.username)
      : organizationPath(author.username);

  const audition = post.audition ?? null;
  const event = post.event ?? null;
  const media = post.media ?? [];
  const links = post.links ?? [];
  const body = post.body?.trim() ? post.body : null;

  // A post whose payload IS a visual (hero audition, hero event, or media)
  // renders as one full-bleed unit: author identity floats INSIDE the
  // visual, and a bottom layer carries actions + caption. Everything else
  // keeps the classic header layout. Audition takes precedence over event
  // when a post somehow carries both.
  const heroAudition =
    audition && auditionEmbedVariant(body ?? '') === 'hero' ? audition : null;
  const heroEvent =
    !heroAudition && event && eventEmbedVariant(body ?? '') === 'hero'
      ? event
      : null;
  const hasResolvableMedia = media.some((m) =>
    m.mediaType === 'video' ? (m.thumbnailUrl ?? m.url) : m.url,
  );
  const heroKind = heroAudition
    ? 'audition'
    : heroEvent
      ? 'event'
      : hasResolvableMedia
        ? 'media'
        : null;

  const handleLike = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      if (isLiked) {
        const result = await unlikePostAction(post.id);
        if (result.success) {
          setIsLiked(false);
          setLikesCount((prev) => Math.max(0, prev - 1));
        } else {
          toast.error(result.error || t('community:feed.unlikeFailed'));
        }
      } else {
        const result = await likePostAction(post.id);
        if (result.success) {
          setIsLiked(true);
          setLikesCount((prev) => prev + 1);
        } else {
          toast.error(result.error || t('community:feed.likeFailed'));
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error(t('community:feed.genericError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleComments = async () => {
    if (showComments) {
      setShowComments(false);
      return;
    }

    setShowComments(true);

    if (comments.length === 0) {
      setLoadingComments(true);
      try {
        const result = await getPostComments(post.id);
        setComments(result.comments);
        setCommentsCount(result.total);
      } catch (error) {
        console.error('Error fetching comments:', error);
        toast.error(t('community:feed.loadCommentsFailed'));
      } finally {
        setLoadingComments(false);
      }
    }
  };

  if (!author) {
    return null;
  }

  // A post with nothing renderable — empty body AND no resolvable embed
  // (e.g. it wraps an audition that has since been archived, which RLS
  // hides from public reads) — would paint just header + actions chrome.
  // Skip it entirely rather than show an empty shell.
  const hasRenderableContent =
    Boolean(body) ||
    Boolean(audition) ||
    Boolean(event) ||
    hasResolvableMedia ||
    links.length > 0;
  if (!hasRenderableContent) {
    return null;
  }

  const authorName = author.full_name || author.username || '?';

  const identityChip = (
    <HeroIdentityChip
      name={authorName}
      avatarUrl={author.avatar_url}
      href={profileLink}
    />
  );

  // Date appears in the row only for the hero layout — the classic layout
  // already carries it in its header line.
  const renderActions = (showDate: boolean) => (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleLike}
        disabled={isSubmitting}
        className={`gap-1.5 px-2.5 ${
          isLiked
            ? 'text-destructive hover:text-destructive'
            : 'text-muted-foreground'
        }`}
        aria-label={
          isLiked
            ? t('community:feed.unlikeAriaLabel')
            : t('community:feed.likeAriaLabel')
        }
        aria-pressed={isLiked}
        title={t('community:feed.like')}
      >
        <Heart
          className="h-4 w-4"
          fill={isLiked ? 'currentColor' : 'none'}
          aria-hidden="true"
        />
        {likesCount > 0 && (
          <span className="text-xs tabular-nums">{likesCount}</span>
        )}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground gap-1.5 px-2.5"
        onClick={handleToggleComments}
        aria-label={t('community:feed.comment')}
        aria-expanded={showComments}
        title={t('community:feed.comment')}
      >
        <MessageCircle className="h-4 w-4" aria-hidden="true" />
        {commentsCount > 0 && (
          <span className="text-xs tabular-nums">{commentsCount}</span>
        )}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground gap-1.5 px-2.5"
        onClick={() => setShowShareDialog(true)}
        aria-label={t('community:feed.share')}
        title={t('community:feed.share')}
      >
        <Share2 className="h-4 w-4" aria-hidden="true" />
      </Button>
      {showDate && (
        <Link
          href={`/home/community/post/${post.id}`}
          className="text-muted-foreground ml-auto text-xs hover:underline"
        >
          {formatTimestampDate(post.created_at)}
        </Link>
      )}
    </div>
  );

  const commentsBlock = showComments && (
    <div className="mt-1">
      {loadingComments ? (
        <div className="text-muted-foreground py-4 text-center text-sm">
          {t('community:feed.loadingComments', 'Loading comments...')}
        </div>
      ) : (
        <CommentsSection
          postId={post.id}
          initialComments={comments}
          currentProfileId={currentProfileId}
          onCommentAdded={() => setCommentsCount((prev) => prev + 1)}
          onCommentDeleted={() =>
            setCommentsCount((prev) => Math.max(0, prev - 1))
          }
        />
      )}
    </div>
  );

  // -------------------------------------------------------------------
  // Hero layout: visual first (identity floats inside), bottom layer with
  // actions + caption + any secondary attachments.
  // -------------------------------------------------------------------
  if (heroKind) {
    return (
      <Card
        className="border-border gap-0 overflow-hidden p-0"
        data-testid="feed-post-card"
      >
        {heroKind === 'audition' ? (
          <AuditionEmbed
            audition={heroAudition!}
            variant="hero"
            identity={identityChip}
          />
        ) : heroKind === 'event' ? (
          <EventEmbed
            event={heroEvent!}
            variant="hero"
            identity={identityChip}
          />
        ) : (
          <PostMediaGrid media={media} identity={identityChip} />
        )}

        {/* Bottom layer: actions + caption */}
        <div className="flex flex-col gap-2 p-3">
          {renderActions(true)}
          {body && (
            <PostBody
              body={body}
              mentions={post.mentions}
              className="text-sm"
            />
          )}
          {/* Secondary attachments not covered by the hero visual */}
          {heroKind !== 'media' && hasResolvableMedia && (
            <PostMediaGrid media={media} />
          )}
          {audition && heroKind !== 'audition' && (
            <AuditionEmbed audition={audition} variant="chip" />
          )}
          {event && heroKind !== 'event' && (
            <EventEmbed event={event} variant="chip" />
          )}
          {links.map((link, index) => (
            <LinkAttachment
              key={`${link.href}-${index}`}
              href={link.href}
              preview={link.preview}
            />
          ))}
          {commentsBlock}
        </div>

        {/* Share dialog */}
        <ShareDialog
          postId={post.id}
          postUrl={postUrl}
          postBody={post.body}
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
        />
      </Card>
    );
  }

  // -------------------------------------------------------------------
  // Classic layout: header row, body, chip/link attachments, actions.
  // -------------------------------------------------------------------
  return (
    <Card className="border-border p-4" data-testid="feed-post-card">
      <div className="flex gap-3">
        {/* Avatar */}
        <Link href={profileLink} className="shrink-0">
          <Avatar className="h-10 w-10 cursor-pointer">
            <AvatarImage src={author.avatar_url || undefined} />
            <AvatarFallback>
              {authorName[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </Link>

        {/* Content */}
        <div className="min-w-0 flex-1 space-y-3">
          {/* Header: name @handle · date on one wrapping line */}
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
            <Link
              href={profileLink}
              className="text-foreground truncate font-semibold hover:underline"
            >
              {author.full_name || author.username}
            </Link>
            <span className="text-muted-foreground truncate text-sm">
              @{author.username}
            </span>
            <span className="text-muted-foreground text-sm" aria-hidden="true">
              &middot;
            </span>
            <Link
              href={`/home/community/post/${post.id}`}
              className="text-muted-foreground text-sm whitespace-nowrap hover:underline"
            >
              {formatTimestampDate(post.created_at)}
            </Link>
          </div>

          {/* Content text */}
          {body && <PostBody body={body} mentions={post.mentions} />}

          {/* Linked audition / competition (chip inside longer prose) */}
          {audition && <AuditionEmbed audition={audition} variant="chip" />}

          {/* Linked community event (chip inside longer prose) */}
          {event && <EventEmbed event={event} variant="chip" />}

          {/* Link attachments */}
          {links.length > 0 && (
            <div className="flex flex-col gap-2">
              {links.map((link, index) => (
                <LinkAttachment
                  key={`${link.href}-${index}`}
                  href={link.href}
                />
              ))}
            </div>
          )}

          {/* Actions with inline counts */}
          <div className="border-border border-t pt-2">
            {renderActions(false)}
          </div>

          {/* Comments section */}
          {commentsBlock}
        </div>
      </div>

      {/* Share dialog */}
      <ShareDialog
        postId={post.id}
        postUrl={postUrl}
        postBody={post.body}
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
      />
    </Card>
  );
}
